/**
 * Created by neo on 30.12.22.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ExploreCategory, ExploreCategoryType } from '../../../../Model/Explore/ExploreCategory';
import { useLanguage } from '../../../../Hooks/useLanguage';
import { useLocalized } from '../../../../Hooks/useLocalized';
import { ExploreEntry } from '../../../../Model/Explore/ExploreEntry';
import { ExploreEntryBuilder } from '../../../../Model/Explore/ExploreEntryBuilder';
import { ExerciseVariation, ExerciseVariationApi } from '../../../../Model/ProgramPortfolio/ExerciseVariation';
import { Recipe, RecipeApi } from '../../../../Model/Diet/Recipe/Recipe';
import { ExploreFilterTagsSection } from '../../ExploreFilterTagsSection';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { MagazineCard } from '../../../../Components/ExploreCards/MagazineCard';
import { ExerciseTileCard } from '../../../../Components/ExploreCards/ExerciseTileCard';
import { ExerciseVideoModal } from '../../../../Components/ExploreCards/ExerciseVideoModal';
import { ExploreCategorySwitch } from '../../../../Components/ExploreCards/ExploreCategorySwitch';
import { TileCard } from '../../../../Components/ExploreCards/TileCard';
import { Theme } from '../../../../Theme/Theme';
import { useTrans } from '../../../../Store/System/LocalizationStore';
import { getAllExploreCategoriesV3, childCategoriesOf } from '../../../../Model/Explore/exploreCategoryTreeV3';
import { isPublished } from '../../../../Utils/isPublished';

const PAGE_SIZE = 30;

const CONTENT_CATEGORY_TYPES: ExploreCategoryType[] = ['preview', 'big_preview'];
const EXERCISE_CATEGORY_TYPES: ExploreCategoryType[] = ['single_exercises'];
const RECIPE_CATEGORY_TYPES: ExploreCategoryType[] = ['recipe_recommendations', 'recipe_favorites', 'recipe_all'];
const NON_CONTENT_CATEGORY_TYPES: ExploreCategoryType[] = [
  'card_link',
  'category_group',
  'link_list',
  'tools',
  'blank',
];

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageWrapper = styled.div`
  padding: 48px 0 64px;

  @media (max-width: 768px) {
    padding: 32px 0 48px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  color: ${Theme.Colors.textColor};
  margin: 0 0 12px 0;
`;

const Description = styled.p`
  font-size: 1rem;
  color: ${Theme.Colors.charcoal};
  line-height: 1.6;
  margin: 0;
  max-width: 600px;
`;

const FiltersContainer = styled.div`
  margin-bottom: 32px;
`;

const EntriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ExercisesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const RecipesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${Theme.Colors.charcoal};
  font-size: 1rem;
`;

const SearchContainer = styled.div`
  margin-bottom: 24px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 2px solid ${Theme.Colors.lighterGrey};
  border-radius: 12px;
  font-size: 1rem;
  background: white;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${Theme.Colors.primary};
    box-shadow: 0 0 0 3px ${Theme.Colors.primary}20;
  }

  &::placeholder {
    color: ${Theme.Colors.charcoal};
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: ${Theme.Colors.charcoal};
  pointer-events: none;
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
  color: ${Theme.Colors.charcoal};
`;

const LoadMoreTrigger = styled.div`
  height: 1px;
  width: 100%;
`;

const ResultsInfo = styled.div`
  font-size: 0.875rem;
  color: ${Theme.Colors.charcoal};
  margin-bottom: 16px;
`;

const EntryItem = styled.div<{ delay: number }>`
  animation: ${fadeInUp} 0.5s ease-out forwards;
  animation-delay: ${({ delay }) => delay}ms;
  opacity: 0;
`;

export type ExploreContentViewCategoryRendererProps = {
  category: ExploreCategory;
  linkPrefix?: string;
};

export const ExploreContentViewCategoryRenderer: React.FC<ExploreContentViewCategoryRendererProps> = observer(
  ({ category, linkPrefix }) => {
    const { language } = useLanguage();
    const { t } = useTrans();
    const title = useLocalized(category.name);
    const description = useLocalized(category.description);

    const [entries, setEntries] = useState<ExploreEntry[]>([]);
    const [isLoadingEntries, setIsLoadingEntries] = useState(true);
    const [childCategories, setChildCategories] = useState<ExploreCategory[]>([]);
    const [isLoadingChildren, setIsLoadingChildren] = useState(true);
    const [exercises, setExercises] = useState<ExerciseVariation[]>([]);
    const [recipes, setRecipes] = useState<Recipe[]>([]);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [selectedExercise, setSelectedExercise] = useState<ExerciseVariation | null>(null);
    const [isExerciseModalOpen, setIsExerciseModalOpen] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [exercisePage, setExercisePage] = useState(0);
    const [hasMoreExercises, setHasMoreExercises] = useState(true);
    const [isLoadingExercises, setIsLoadingExercises] = useState(false);
    const [isLoadingMoreExercises, setIsLoadingMoreExercises] = useState(false);

    const abortControllerRef = useRef<AbortController | null>(null);
    const loadMoreRef = useRef<HTMLDivElement>(null);
    const categoryType = category.type as ExploreCategoryType;

    const isExerciseCategory = EXERCISE_CATEGORY_TYPES.includes(categoryType);
    const isRecipeCategory = RECIPE_CATEGORY_TYPES.includes(categoryType);
    const isContentCategory = CONTENT_CATEGORY_TYPES.includes(categoryType);
    const isNonContentCategory = NON_CONTENT_CATEGORY_TYPES.includes(categoryType);

    const fetchExercises = useCallback(
      async (query: string, page: number, append: boolean = false) => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        if (append) {
          setIsLoadingMoreExercises(true);
        } else {
          setIsLoadingExercises(true);
        }

        try {
          const result = await ExerciseVariationApi.find({
            query: query || undefined,
            tags: category.identifier ? [category.identifier] : [],
            size: PAGE_SIZE,
            page,
            lang: language,
          });

          if (append) {
            setExercises((prev) => [...prev, ...result]);
          } else {
            setExercises(result);
          }
          setHasMoreExercises(result.length === PAGE_SIZE);
        } catch (err) {
          if ((err as Error).name !== 'AbortError') {
            console.error('Error fetching exercises:', err);
          }
        } finally {
          setIsLoadingExercises(false);
          setIsLoadingMoreExercises(false);
        }
      },
      [category.identifier, language],
    );

    // Sub-categories: V3 entries live one level deeper for group-style categories,
    // so a category page must always surface its direct children as well.
    useEffect(() => {
      setIsLoadingChildren(true);
      getAllExploreCategoriesV3()
        .then((all) => setChildCategories(childCategoriesOf(all, category.id).filter(isPublished)))
        .catch(() => setChildCategories([]))
        .finally(() => setIsLoadingChildren(false));
    }, [category.id]);

    useEffect(() => {
      if (isNonContentCategory) {
        return;
      }

      if (isExerciseCategory) {
        setExercisePage(0);
        fetchExercises(searchQuery, 0, false);
      } else if (isRecipeCategory) {
        RecipeApi.search({
          size: 100,
        }).then((res) => setRecipes(res));
      } else {
        setIsLoadingEntries(true);
        ExploreEntryBuilder.find({
          rootCategories: category.id ? [category.id] : [],
          size: 200,
          filterTags: selectedTags,
        })
          .then((res) => setEntries(res))
          .catch(() => setEntries([]))
          .finally(() => setIsLoadingEntries(false));
      }

      return () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }, [category, selectedTags, isExerciseCategory, isRecipeCategory, isNonContentCategory, searchQuery, fetchExercises]);

    useEffect(() => {
      if (!isExerciseCategory || !loadMoreRef.current) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMoreExercises && !isLoadingExercises && !isLoadingMoreExercises) {
            const nextPage = exercisePage + 1;
            setExercisePage(nextPage);
            fetchExercises(searchQuery, nextPage, true);
          }
        },
        { threshold: 0.1 },
      );

      observer.observe(loadMoreRef.current);
      return () => observer.disconnect();
    }, [isExerciseCategory, hasMoreExercises, isLoadingExercises, isLoadingMoreExercises, exercisePage, searchQuery, fetchExercises]);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
      setExercisePage(0);
    }, []);

    const handleSelectFilterTag = useCallback((tags: string[]) => {
      setSelectedTags(tags);
    }, []);

    const handleExerciseClick = useCallback((exercise: ExerciseVariation) => {
      setSelectedExercise(exercise);
      setIsExerciseModalOpen(true);
    }, []);

    const handleCloseExerciseModal = useCallback(() => {
      setIsExerciseModalOpen(false);
      setSelectedExercise(null);
    }, []);

    const switchLinkPrefix = linkPrefix ?? '/academy/';

    const subCategoriesBlock =
      childCategories.length > 0 ? (
        <>
          {childCategories.map((child, index) => (
            <ExploreCategorySwitch
              key={child.id}
              category={child}
              categoryIndex={index}
              isLast={index === childCategories.length - 1}
              linkPrefix={switchLinkPrefix}
            />
          ))}
        </>
      ) : null;

    const emptyState = (
      <EmptyState>
        {t('exploreCategory.noContent', { defaultValue: 'No content available yet.' })}
      </EmptyState>
    );

    if (isNonContentCategory) {
      // category_group & co. hold their content in child categories — this branch
      // is the primary category-page shape in prod, not an error case.
      return (
        <PageWrapper>
          <PageHeader>
            <Title>{title}</Title>
            {description && <Description>{description}</Description>}
          </PageHeader>
          {subCategoriesBlock}
          {!isLoadingChildren && childCategories.length === 0 && emptyState}
        </PageWrapper>
      );
    }

    const renderContent = () => {
      if (isExerciseCategory) {
        return (
          <>
            <SearchContainer>
              <SearchInputWrapper>
                <SearchIcon>🔍</SearchIcon>
                <SearchInput
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={t('exercise.searchPlaceholder', { defaultValue: 'Search exercises...' })}
                />
              </SearchInputWrapper>
            </SearchContainer>
            {!isLoadingExercises && exercises.length > 0 && (
              <ResultsInfo>
                {exercises.length}{hasMoreExercises ? '+' : ''}{' '}
                {t('exercise.resultsLabel', { defaultValue: 'exercises found' })}
              </ResultsInfo>
            )}
            {isLoadingExercises ? (
              <LoadingIndicator>
                {t('common.loading', { defaultValue: 'Loading...' })}
              </LoadingIndicator>
            ) : exercises.length === 0 ? (
              <EmptyState>
                {searchQuery
                  ? t('exercise.noResults', { defaultValue: 'No exercises found for your search.' })
                  : t('exercise.noExercises', { defaultValue: 'No exercises available.' })}
              </EmptyState>
            ) : (
              <ExercisesGrid>
                {exercises.map((exercise, index) => (
                  <EntryItem key={exercise.id} delay={Math.min(index * 30, 300)}>
                    <ExerciseTileCard exercise={exercise} onClick={handleExerciseClick} />
                  </EntryItem>
                ))}
              </ExercisesGrid>
            )}
            <LoadMoreTrigger ref={loadMoreRef} />
            {isLoadingMoreExercises && (
              <LoadingIndicator>
                {t('common.loadingMore', { defaultValue: 'Loading more...' })}
              </LoadingIndicator>
            )}
          </>
        );
      }

      if (isRecipeCategory) {
        return (
          <RecipesGrid>
            {recipes.map((recipe, index) => (
              <EntryItem key={recipe.id} delay={Math.min(index * 50, 500)}>
                <TileCard entry={recipe} linkPrefix="/recipe/" />
              </EntryItem>
            ))}
          </RecipesGrid>
        );
      }

      return (
        <>
          {subCategoriesBlock}
          <EntriesGrid>
            {entries.map((entry, index) => (
              <EntryItem key={entry.id} delay={Math.min(index * 50, 500)}>
                <MagazineCard entry={entry} linkPrefix={linkPrefix} />
              </EntryItem>
            ))}
          </EntriesGrid>
          {!isLoadingEntries && !isLoadingChildren && entries.length === 0 && childCategories.length === 0 && emptyState}
        </>
      );
    };

    return (
      <PageWrapper>
        <PageHeader>
          <Title>{title}</Title>
          {description && <Description>{description}</Description>}
        </PageHeader>
        {isContentCategory && (
          <FiltersContainer>
            <ExploreFilterTagsSection
              category={category.identifier}
              selectedTags={selectedTags}
              onFilter={handleSelectFilterTag}
            />
          </FiltersContainer>
        )}
        {renderContent()}
        {isExerciseCategory && (
          <ExerciseVideoModal
            isOpen={isExerciseModalOpen}
            onClose={handleCloseExerciseModal}
            exercise={selectedExercise}
          />
        )}
      </PageWrapper>
    );
  },
);
